import React, { useState, useCallback } from 'react';

import { getEnv } from '@config';
import { useHistory } from 'react-router-dom';
import { useAuth } from '@hooks';
import * as andesService from '@services/andes-service';

import {
  fetchPromotions,
  fetchTokenForCreditLineWidget,
  fetchTokenForInstallmentCreditWidget,
  setIsLoadingCreditLineToken,
  setIsLoadingInstallmentCreditToken,
  setTokenForCreditLine,
  setTokenForInstallmentCredit,
  fetchTokenForCreditWithAccountWidget,
  setIsLoadingCreditWithAccountToken,
  setTokenForCreditWithAccount,
} from '@store/promotions/actions';

import { useDispatch, useSelector } from 'react-redux';

import { FINANCE, FUNDS_OUT, HOME } from '@constants/routes';
import { FinancingBanner } from '@screen';

const usePromotions = () => {
  const { isAvailableWidget } = getEnv();
  const [loadingSimulationCredit, setLoadingSimulationCredit] = useState(true)
  const [installmentsSimulation, setInstallmentsSimulation] = useState(null)
  const { isSeller } = useAuth();
  const { merchant: loginMerchant } = useSelector((state) => state.login);
  const promotions = useSelector((state) => state.promotions.promotions);
  const tokenForCreditLineWidget = useSelector(
    (state) => state.promotions.tokenForCreditLineWidget
  );
  const tokenForCreditWithAccountWidget = useSelector(
    (state) => state.promotions.tokenForCreditWithAccountWidget
  );
  const tokenForInstallmentCreditWidget = useSelector(
    (state) => state.promotions.tokenForInstallmentCreditWidget
  );
  const isPromotionsLoading = useSelector((state) => state.promotions.isPromotionsLoading);
  const isCreditLineTokenLoading = useSelector(
    (state) => state.promotions.isCreditLineTokenLoading
  );
  const isCreditWithAccountTokenLoading = useSelector(
    (state) => state.promotions.isCreditWithAccountTokenLoading
  );
  const isInstallmentCreditTokenLoading = useSelector(
    (state) => state.promotions.isInstallmentCreditTokenLoading
  );
  const dispatch = useDispatch();
  const history = useHistory();

  const getPromotions = useCallback(
    () => dispatch(fetchPromotions()),
    // eslint-disable-next-line
    [loginMerchant, dispatch]
  );

  const getTokenForCreditLineWidget = useCallback(
    // eslint-disable-next-line no-sequences
    () => (dispatch(setIsLoadingCreditLineToken(true)), dispatch(fetchTokenForCreditLineWidget())),
    // eslint-disable-next-line
    [loginMerchant, dispatch]
  );

  const setTokenForCreditLineLoading = useCallback(
    // eslint-disable-next-line no-sequences
    () => (dispatch(setIsLoadingCreditLineToken(true)), dispatch(setTokenForCreditLine(null))),
    // eslint-disable-next-line
    [dispatch]
  );

  const getTokenForCreditWithAccountWidget = useCallback(
    // eslint-disable-next-line no-sequences
    () => (
      dispatch(setIsLoadingCreditWithAccountToken(true)),
      dispatch(fetchTokenForCreditWithAccountWidget())
    ),
    // eslint-disable-next-line
    [loginMerchant, dispatch]
  );

  const setTokenForCreditWithAccountLoading = useCallback(
    // eslint-disable-next-line no-sequences
    () => (
      dispatch(setIsLoadingCreditWithAccountToken(true)),
      dispatch(setTokenForCreditWithAccount(null))
    ),
    // eslint-disable-next-line
    [dispatch]
  );

  const getTokenForInstallmentCreditWidget = useCallback(
    // eslint-disable-next-line no-sequences
    () => (
      dispatch(setIsLoadingInstallmentCreditToken(true)),
      dispatch(fetchTokenForInstallmentCreditWidget())
    ),
    // eslint-disable-next-line
    [loginMerchant, dispatch]
  );

  const setTokenForInstallmentCreditLoading = useCallback(
    // eslint-disable-next-line no-sequences
    () => (
      dispatch(setIsLoadingInstallmentCreditToken(true)),
      dispatch(setTokenForInstallmentCredit(null))
    ),
    // eslint-disable-next-line
    [dispatch]
  );

  const getPromotionsBanner = ({ showModalToBank, showModalAdvancement }, title) => {
    const config = {
      isSeller: isSeller(),
      callsToAction: {
        toExternalUrl: showModalToBank,
        toWidget: () => history.push(FINANCE),
        toFoundOut: () => history.push(FUNDS_OUT),
        toQr: () => history.push(HOME),
        showModalAdvancement,
      },
      title,
    };
    return <FinancingBanner config={config} />;
  };

  const promotionEnablesWidget = () => {
    if (!promotions || promotions.length === 0) {
      return false;
    }

    return promotions.some(
      (promo) =>
        isAvailableWidget[promo.product] != null &&
        isAvailableWidget[promo.product] !== undefined &&
        isAvailableWidget[promo.product].some(
          (pc) => pc.process === promo.process && pc.state === promo.state
        )
    );
  };

  const getInstallmentsSimulation = (financedAmount) => {
    setLoadingSimulationCredit(true);
    andesService.postInstallmentsSimulate(financedAmount)
      .then((res) => {
        const {
          installments: installments,
          installment_value: installmentValue,
          total_credit_amount: totalCreditAmount,
          interest_rate_value: interestRateValue,
        } = res.data
        setInstallmentsSimulation({
          installments,
          installmentValue,
          totalCreditAmount,
          interestRateValue,
        })
      })
      .finally(() => {
        setLoadingSimulationCredit(false);
      });
  };

  return {
    isPromotionsLoading,
    isCreditLineTokenLoading,
    isInstallmentCreditTokenLoading,
    isCreditWithAccountTokenLoading,
    getPromotions,
    getTokenForCreditLineWidget,
    setTokenForCreditLineLoading,
    setTokenForCreditWithAccountLoading,
    getTokenForCreditWithAccountWidget,
    getPromotionsBanner,
    promotionEnablesWidget,
    promotions,
    tokenForCreditLineWidget,
    tokenForInstallmentCreditWidget,
    tokenForCreditWithAccountWidget,
    getTokenForInstallmentCreditWidget,
    setTokenForInstallmentCreditLoading,
    loadingSimulationCredit,
    installmentsSimulation,
    getInstallmentsSimulation,
  };
};

export default usePromotions;
